import React, { useMemo } from "react";
import { connect, useSelector } from "react-redux";
import SideNav from "components/layout-components/SideNav";
import TopNav from "components/layout-components/TopNav";
import MobileNav from "components/layout-components/MobileNav";
import HeaderNav from "components/layout-components/HeaderNav";
import { PageCrumbProvider } from "../../data/hooks/usePageCrumb";
import PageHeader from "components/layout-components/PageHeader";
import Footer from "components/layout-components/Footer";
import AppViews from "views/app-views";
import { Grid, Layout } from "antd";

import navigationConfig from "configs/NavigationConfig";
import { NAV_TYPE_SIDE, NAV_TYPE_TOP } from "constants/ThemeConstant";
import utils from "utils";
import { useFirestoreConnect } from "react-redux-firebase";
import {
  FIRESTORE_CARTS_TABLE,
  FIRESTORE_CATEGORIES_TABLE,
  FIRESTORE_CMS_ROLES_TABLE,
  FIRESTORE_COLLECTIONS_TABLE,
  FIRESTORE_CONFIG,
  FIRESTORE_DEALERS_TABLE,
  FIRESTORE_DISCOUNT_CODES_TABLE,
  FIRESTORE_FEATURED_PRODUCTS_TABLE,
  FIRESTORE_GROUPS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_PRODUCTS_TABLE,
  FIRESTORE_PROMOTIONS_TABLE,
  FIRESTORE_PURCHASE_ORDERS_TABLE,
  FIRESTORE_USERS_TABLE,
} from "../../constants/FirestoreConstant";
import * as dayjs from "dayjs";
import { usePermissions } from "../../data/hooks/usePermissions";
import { PERMISSIONS } from "../../functions/permissions";

const { Content } = Layout;
const { useBreakpoint } = Grid;

export const AppLayout = ({ navCollapsed, navType, location, direction }) => {
  const queryModifications = useSelector((state) => state.queries);
  const { can, loading: permissionsLoading } = usePermissions();

  // Welke collecties naar de browser gaan hangt af van wat de gebruiker mag.
  // Tot BMX-35 laadde iedereen alles (alle orders, inkoop, dealers, ...). Nu
  // alleen wat de eigen permissies nodig hebben — een verkoper krijgt klanten,
  // winkelwagens, producten (voor de orderdetails) en orders; de rest niet.
  // De landfiltering zelf zit niet hier maar in useScopedCustomers /
  // useScopedOrders: een Firestore `in`-query op land naast de bestaande
  // filters zou een samengestelde index vereisen die we vanuit de repo niet
  // kunnen aanmaken.
  const allQueries = useMemo(() => {
    // De rollen (cmsRoles) altijd, ook zolang de permissies nog laden: daar
    // wachten de permissies juist op. Beheer → Rollen schrijft erin.
    const roleQuery = { collection: FIRESTORE_CMS_ROLES_TABLE, storeAs: "cmsRoles" };
    if (permissionsLoading) return [roleQuery];
    const queries = [
      roleQuery,
      { collection: FIRESTORE_PRODUCTS_TABLE },
      {
        collection: FIRESTORE_USERS_TABLE,
        where: ["role", "==", "webshopUser"],
        storeAs: "webshopUsers",
      },
      {
        collection: FIRESTORE_USERS_TABLE,
        where: ["isSalesPerson", "==", true],
        storeAs: "salesPersons",
      },
      { collection: FIRESTORE_CARTS_TABLE },
      { collection: FIRESTORE_CONFIG },
    ];
    if (can(PERMISSIONS.DISCOUNTS_MANAGE)) {
      queries.push({ collection: FIRESTORE_PROMOTIONS_TABLE }, { collection: FIRESTORE_DISCOUNT_CODES_TABLE });
    }
    if (can(PERMISSIONS.ADMIN_GROUPS) || can(PERMISSIONS.ADMIN_COLLECTIONS) || can(PERMISSIONS.PRODUCTS_MANAGE)) {
      queries.push({ collection: FIRESTORE_GROUPS_TABLE }, { collection: FIRESTORE_COLLECTIONS_TABLE });
    }
    if (can(PERMISSIONS.PRODUCTS_MANAGE)) {
      queries.push({ collection: FIRESTORE_FEATURED_PRODUCTS_TABLE });
    }
    if (
      can(PERMISSIONS.PRODUCTS_MANAGE) ||
      can(PERMISSIONS.CATEGORIES_MANAGE) ||
      can(PERMISSIONS.PURCHASING_MANAGE) ||
      can(PERMISSIONS.REPORTS_VIEW)
    ) {
      queries.push({ collection: FIRESTORE_CATEGORIES_TABLE, orderBy: ["position", "asc"] });
    }
    if (can(PERMISSIONS.PURCHASING_MANAGE) || can(PERMISSIONS.PRODUCTS_MANAGE)) {
      queries.push({ collection: FIRESTORE_PURCHASE_ORDERS_TABLE });
    }
    if (can(PERMISSIONS.DEALERS_MANAGE)) {
      queries.push({ collection: FIRESTORE_DEALERS_TABLE });
    }
    if (can(PERMISSIONS.ORDERS_VIEW)) {
      // Standaard de laatste N dagen (state.queries.ordersMaxDaysAgo); de
      // orderlijst kan dat opheffen met "alle orders ophalen".
      if (!!queryModifications?.ordersMaxDaysAgo) {
        const orderDaysAgoTimestamp = dayjs().subtract(queryModifications.ordersMaxDaysAgo, "days").unix();
        queries.push({ collection: FIRESTORE_ORDERS_TABLE, where: ["createdAt", ">=", orderDaysAgoTimestamp] });
      } else {
        queries.push({ collection: FIRESTORE_ORDERS_TABLE });
      }
    }
    return queries;
  }, [can, permissionsLoading, queryModifications]);

  // Fetch all collections from Firestore and put in redux store
  useFirestoreConnect(allQueries);
  const currentRouteInfo = utils.getRouteInfo(navigationConfig, location.pathname);
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes("lg");
  const isNavSide = navType === NAV_TYPE_SIDE;
  const isNavTop = navType === NAV_TYPE_TOP;

  return (
    // Eén provider om header en pagina's: HeaderNav leest het kruimellabel dat
    // de actieve detailpagina publiceert.
    <PageCrumbProvider>
      <Layout style={{ height: "100vh" }}>
        <HeaderNav isMobile={isMobile} />
        {isNavTop && !isMobile ? <TopNav /> : null}
        <Layout className="app-container" style={{ flex: 1, overflow: "hidden" }}>
          {isNavSide && !isMobile ? <SideNav /> : null}
          <Layout className="app-layout" style={{ paddingLeft: 0, flex: 1, display: "flex", flexDirection: "column" }}>
            <div className={`app-content ${isNavTop ? "layout-top-nav" : ""}`} style={{ flex: 1, overflow: "auto" }}>
              <PageHeader display={currentRouteInfo?.breadcrumb} title={currentRouteInfo?.title} />
              <Content style={{ height: "100%" }}>
                <AppViews />
              </Content>
            </div>
            <Footer />
          </Layout>
        </Layout>
        {isMobile && <MobileNav />}
      </Layout>
    </PageCrumbProvider>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, locale } = theme;
  return { navCollapsed, navType, locale };
};

export default connect(mapStateToProps)(React.memo(AppLayout));
